import moment from "moment";

export const prettyTime = (t) =>
  t ? moment(String(t).slice(0, 5), "HH:mm").format("h:mma") : "";

export const prettyRange = (st, et) => {
  if (!st && !et) return "";
  if (!st || !et) return "Working";
  return `${prettyTime(st)}–${prettyTime(et)}`;
};

/**
 * Take a single day's availability object from the backend and produce
 * a structured display object that both the AssigneePickerModal and the
 * Availability Calendar can consume.
 */
export const buildScheduleFromDateAvail = (dateAvail) => {
  const empty = {
    status: "no_data",
    availability: [],
    busy: [],
    unscheduledCount: 0,
    unscheduledPreview: "",
    timeOff: [],
  };

  if (!dateAvail) return empty;

  const fmtTime = (t) => prettyTime(t);

  /** Map an array of engagement objects into busy-item display entries. */
  const engagementsToBusy = (engagements) =>
    (engagements || []).map((eng) => ({
      time: `${fmtTime(eng.start_time)}–${fmtTime(eng.end_time)}`,
      icon: "/images/task-icon.svg",
      desc: eng?.title || "Work order",
    }));

  const result = {
    status: dateAvail.status || "unknown",
    availability: [],
    busy: [],
    unscheduledCount: 0,
    unscheduledPreview: "",
    timeOff: [],
  };

  // ── Off ────────────────────────────────────────────────────────────
  if (dateAvail.status === "off") {
    result.timeOff.push({
      time: "All day",
      icon: "",
      desc: dateAvail.reason || "Off",
    });

    result.busy.push(...engagementsToBusy(dateAvail.engagements));
  }
  // ── No schedule (contacts) ─────────────────────────────────────────
  else if (dateAvail.status === "no_schedule") {
    result.busy.push(...engagementsToBusy(dateAvail.engagements));
  }
  // ── Working / normal ───────────────────────────────────────────────
  else {
    if (dateAvail.available_slots && dateAvail.available_slots.length > 0) {
      dateAvail.available_slots.forEach((slot) => {
        result.availability.push({
          time: `${fmtTime(slot.start_time)}–${fmtTime(slot.end_time)}`,
          icon: "/images/available.svg",
          desc: "Available",
        });
      });
    }

    result.busy.push(...engagementsToBusy(dateAvail.engagements));
  }

  // ── Unscheduled work orders (all statuses) ─────────────────────────
  if (
    Array.isArray(dateAvail.unscheduled_work_orders) &&
    dateAvail.unscheduled_work_orders.length > 0
  ) {
    const wos = dateAvail.unscheduled_work_orders;
    result.unscheduledCount = wos.length;

    const titles = wos
      .map((wo) => wo?.title || wo?.summary || "Work order")
      .filter(Boolean);
    const preview = titles.slice(0, 2).join(", ");
    const more = titles.length > 2 ? ` +${titles.length - 2} more` : "";
    result.unscheduledPreview = preview + more;
  }

  return result;
};

/**
 * Flatten the nested backend availability response into one row per
 * assignee-date combination (used by the Availability Calendar grid).
 */
export const transformAvailabilityData = (backendData) => {
  if (!Array.isArray(backendData)) return [];

  const rows = [];

  backendData.forEach((assignee) => {
    if (!assignee.availability || !Array.isArray(assignee.availability)) return;

    assignee.availability.forEach((dateAvail) => {
      rows.push({
        assignee_id: assignee.assignee_id,
        assignee_type: assignee.assignee_type,
        name: assignee.name || "—",
        email: assignee.email || "",
        work_date: dateAvail.date,
        status: dateAvail.status,
        available_slots: dateAvail.available_slots || [],
        engagements: dateAvail.engagements || [],
        unscheduled_work_orders: dateAvail.unscheduled_work_orders || [],
        reason: dateAvail.reason || "",
      });
    });
  });

  return rows;
};
